import { useMutation, useQuery } from '@tanstack/vue-query'
import { httpRequest } from '@shared/api/httpRequests'
import { urlSubst } from '@shared/utils/url'

/**
 * `useTriggerRule(ruleName, params, queryOptions)` - useQuery wrapper around the
 * legacy `/Queue/{orgId}/PageEvent/{ruleIdOrName}` endpoint. Used by PG&E (and
 * potentially other customers) to drive page-load rules from a Vue3 view.
 */
export function useTriggerRule (ruleName, params = {}, queryOptions = {}) {
  const orgId = window.siteVars?.organization?.id || 1
  const queryKey = ['useTriggerRule', orgId, ruleName, params]
  const queryFn = async () => {
    const response = await httpRequest({
      method: 'POST',
      url: `/Queue/${orgId}/PageEvent/${ruleName}`,
      data: params,
    })
    if (!response || !response.enum) {
      throw new Error(`Rule '${ruleName}' returned an invalid response`)
    }
    if (response.enum.error) {
      throw new Error(response.enum.error)
    }
    return response.enum
  }
  return useQuery({ queryKey, queryFn, ...queryOptions })
}

/**
 * @constant {string} BASE_URL Base URL path
 */
const BASE_URL = '/CompanyManagement/api'

/**
 * @constant {Object.<string:string>} API Map of API CRUD methods and their relative URLs (not including BASE_URL)
 */
export const API = {
  APPROVE_COMPANY: '/v1/approve-company/:companyName',
  APPROVE_COMPANY_MEMBER: '/v1/company/:companyName/member/:userName/approve',
  CREATE_COMPANY_EMAIL_GROUP: '/v1/company/:companyId/company-email-groups',
  CREATE_COMPANY_EMAIL_GROUP_MEMBER: '/v1/company/:companyId/company-email-groups/:companyEmailGroupId/members',
  DELETE_COMPANY: '/v1/delete-company/:companyName',
  DELETE_COMPANY_EMAIL_GROUP: '/v1/company/:companyId/company-email-groups/:companyEmailGroupId',
  DELETE_COMPANY_EMAIL_GROUP_MEMBER: '/v1/company/:companyId/company-email-groups/:companyEmailGroupId/members/:companyEmailGroupMemberId',
  DELETE_COMPANY_MEMBER: '/v1/company/:companyName/member/:userName',
  DISABLE_COMPANY: '/v1/disable-company/:companyName',
  DISABLE_COMPANY_MEMBER: '/v1/company/:companyName/member/:userName/disable',
  ENABLE_COMPANY: '/v1/enable-company/:companyName',
  GET_COMPANIES: '/v1/companies',
  GET_COMPANIES_V2: '/v2/companies',
  GET_COMPANY: '/v2/company/:companyId/',
  GET_COMPANY_EMAIL_GROUP_MEMBERS: '/v1/company/:companyId/company-email-groups/:companyEmailGroupId/members',
  GET_COMPANY_EMAIL_GROUPS: '/v1/company/:companyId/company-email-groups',
  GET_COMPANY_MEMBERS: '/v2/company/:companyId/members',
  GET_COMPANY_MEMBERS_LIST: '/v1/company/:companyId/members',
  GET_HISTORY: '/audit/companies',
  TOGGLE_COMPANY_MEMBER_APPLICATION_ACCESS: '/v1/limit-access/:companyName/:userName',
  LIMIT_COMPANY_MEMBER_APPLICATION_ACCESS: '/v1/limit-access/:companyName/:userName',
  FULL_COMPANY_MEMBER_APPLICATION_ACCESS: '/v1/full-access/:companyName/:userName',
  TOGGLE_COMPANY_MEMBER_ROLE: '/v1/company/:companyName/member/:userName/toggleRole',
  UPDATE_COMPANY_EMAIL_GROUP: '/v1/company/:companyId/company-email-groups/:companyEmailGroupId',
  UPDATE_COMPANY_EMAIL_GROUP_MEMBER: '/v1/company/:companyId/company-email-groups/:companyEmailGroupId/members/:companyEmailGroupMemberId',
}


const syntheticDelay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * @constant {Object.<string:string>} COMPANIES_API Map of API CRUD methods and their URLs (including BASE_URL)
 */
export const COMPANIES_API = Object.fromEntries(
  Object.entries(API).map(([key, val]) => [key, `${BASE_URL}${val}`]),
)

export function approveCompany (companyName, mutationOptions = {}) {
  const mutationFn = async () => await httpRequest({
    method: 'PUT',
    url: urlSubst(COMPANIES_API.APPROVE_COMPANY, { companyName }),
  })
  return useMutation({ mutationFn, ...mutationOptions })
}

export function approveCompanyMember (companyName, userName, mutationOptions = {}) {
  const mutationFn = async () => await httpRequest({
    method: 'PUT',
    url: urlSubst(COMPANIES_API.APPROVE_COMPANY_MEMBER, { companyName, userName }),
  })
  return useMutation({ mutationFn, ...mutationOptions })
}

export function createCompanyEmailGroup (mutationOptions = {}) {
  const mutationFn = async ({companyId, companyName}) => {
    return await httpRequest({
      method: 'POST',
      url: urlSubst(COMPANIES_API.CREATE_COMPANY_EMAIL_GROUP, { companyId }),
      data: { name: companyName },
    })
  }

  return useMutation({ mutationFn, ...mutationOptions })
}

export function createCompanyEmailGroupMember (mutationOptions = {}) {
  const mutationFn = async ({companyId, companyEmailGroupId, email}) => {
    return await httpRequest({
      method: 'POST',
      url: urlSubst(COMPANIES_API.CREATE_COMPANY_EMAIL_GROUP_MEMBER, { companyId, companyEmailGroupId }),
      data: { email },
    })
  }

  return useMutation({ mutationFn, ...mutationOptions })
}

export function deleteCompany (companyName, mutationOptions = {}) {
  const mutationFn = async () => await httpRequest({
    method: 'DELETE',
    url: urlSubst(COMPANIES_API.DELETE_COMPANY, { companyName }),
  })
  return useMutation({ mutationFn, ...mutationOptions })
}

export function deleteCompanyEmailGroup(queryOptions = {}) {
  const mutationFn = async ({companyId, companyEmailGroupId}) => {
    return await httpRequest({
      method: 'DELETE',
      url: urlSubst(COMPANIES_API.DELETE_COMPANY_EMAIL_GROUP, { companyId, companyEmailGroupId }),
    })
  }
  return useMutation({ mutationFn, ...queryOptions })
}

export function deleteCompanyEmailGroupMember(queryOptions = {}) {
  const mutationFn = async ({companyId, companyEmailGroupId, companyEmailGroupMemberId}) => {
    return await httpRequest({
      method: 'DELETE',
      url: urlSubst(COMPANIES_API.DELETE_COMPANY_EMAIL_GROUP_MEMBER, { companyId, companyEmailGroupId, companyEmailGroupMemberId }),
    })
  }
  return useMutation({ mutationFn, ...queryOptions })
}

export function deleteCompanyMember (companyName, userName, mutationOptions = {}) {
  const mutationFn = async () => await httpRequest({
    method: 'DELETE',
    url: urlSubst(COMPANIES_API.DELETE_COMPANY_MEMBER, { companyName, userName }),
  })
  return useMutation({ mutationFn, ...mutationOptions })
}

export function disableCompany (companyName, mutationOptions = {}) {
  const mutationFn = async () => await httpRequest({
    method: 'PUT',
    url: urlSubst(COMPANIES_API.DISABLE_COMPANY, { companyName }),
  })
  return useMutation({ mutationFn, ...mutationOptions })
}

export function disableCompanyMember (companyName, userName, mutationOptions = {}) {
  const mutationFn = async () => await httpRequest({
    method: 'PUT',
    url: urlSubst(COMPANIES_API.DISABLE_COMPANY_MEMBER, { companyName, userName }),
  })
  return useMutation({ mutationFn, ...mutationOptions })
}

export function enableCompany (companyName, mutationOptions = {}) {
  const mutationFn = async () => await httpRequest({
    method: 'PUT',
    url: urlSubst(COMPANIES_API.ENABLE_COMPANY, { companyName }),
  })
  return useMutation({ mutationFn, ...mutationOptions })
}

export function getCompanies (queryOptions = {}) {
  const queryKey = [COMPANIES_API.GET_COMPANIES]
  const queryFn = async () => await httpRequest({
    url: urlSubst(COMPANIES_API.GET_COMPANIES),
  })
  return useQuery({ queryKey, queryFn, ...queryOptions })
}

export function getCompany (companyId, queryOptions = {}) {
  const queryKey = [COMPANIES_API.GET_COMPANY]
  const queryFn = async () => await httpRequest({
    url: urlSubst(COMPANIES_API.GET_COMPANY, { companyId }),
  })
  return useQuery({ queryKey, queryFn, ...queryOptions })
}

export function getCompanyMembersList (companyId, queryOptions = {}) {
  const queryKey = [COMPANIES_API.GET_COMPANY_MEMBERS_LIST, companyId]
  const queryFn = async () => await httpRequest({
    url: urlSubst(COMPANIES_API.GET_COMPANY_MEMBERS_LIST, { companyId }),
  })
  return useQuery({ queryKey, queryFn, ...queryOptions })
}

export function getCompanyEmailGroupMembers(companyId, companyEmailGroupId, queryOptions = {}) {
  const queryKey = [COMPANIES_API.GET_COMPANY_EMAIL_GROUP_MEMBERS, companyId, companyEmailGroupId]
  const queryFn = async () => {
    const [apiResultPromise] = await Promise.all([
      httpRequest({
        method: 'GET',
        url: urlSubst(COMPANIES_API.GET_COMPANY_EMAIL_GROUP_MEMBERS, { companyId, companyEmailGroupId }),
      }),
      syntheticDelay(),
    ])
    return apiResultPromise
  }
  return useQuery({ queryKey, queryFn, ...queryOptions })
}

export function getCompanyEmailGroups(companyId, queryOptions = {}) {
  const queryKey = [COMPANIES_API.GET_COMPANY_EMAIL_GROUPS, companyId]
  const queryFn = async () => {
    const [apiResultPromise] = await Promise.all([
      httpRequest({
        method: 'GET',
        url: urlSubst(COMPANIES_API.GET_COMPANY_EMAIL_GROUPS, { companyId }),
      }),
      syntheticDelay(),
    ])
    return apiResultPromise
  }
  return useQuery({ queryKey, queryFn, ...queryOptions })
}

export function toggleApplicationAccess (companyName, userName, mutationOptions = {}) {
  const mutationFn = async (body) => {
    return await httpRequest({
      method: 'POST',
      url: urlSubst(COMPANIES_API.LIMIT_COMPANY_MEMBER_APPLICATION_ACCESS, { companyName, userName }),
      data: body,
    })
  }

  return useMutation({ mutationFn, ...mutationOptions })
}

export function limitApplicationAccess (companyName, userName, mutationOptions = {}) {
  const mutationFn = async (body) => {
    return await httpRequest({
      method: 'POST',
      url: urlSubst(COMPANIES_API.LIMIT_COMPANY_MEMBER_APPLICATION_ACCESS, { companyName, userName }),
      data: body,
    })
  }

  return useMutation({ mutationFn, ...mutationOptions })
}

export function fullApplicationAccess (companyName, userName, mutationOptions = {}) {
  const mutationFn = async (body) => {
    return await httpRequest({
      method: 'POST',
      url: urlSubst(COMPANIES_API.FULL_COMPANY_MEMBER_APPLICATION_ACCESS, { companyName, userName }),
      data: body,
    })
  }

  return useMutation({ mutationFn, ...mutationOptions })
}

export function toggleCompanyMemberRole (companyName, userName, mutationOptions = {}) {
  const mutationFn = async () => await httpRequest({
    method: 'PUT',
    url: urlSubst(COMPANIES_API.TOGGLE_COMPANY_MEMBER_ROLE, { companyName, userName }),
  })
  return useMutation({ mutationFn, ...mutationOptions })
}

export function updateCompanyEmailGroup (companyId, companyEmailGroupId, mutationOptions = {}) {
  const mutationFn = async ({emailGroupName}) => {
    return await httpRequest({
      method: 'PATCH',
      url: urlSubst(COMPANIES_API.UPDATE_COMPANY_EMAIL_GROUP, { companyId, companyEmailGroupId }),
      data: {
        name: emailGroupName,
      },
    })
  }

  return useMutation({ mutationFn, ...mutationOptions })
}

export function updateCompanyEmailGroupMember (companyId, companyEmailGroupId, companyEmailGroupMemberId, mutationOptions = {}) {
  const mutationFn = async ({email}) => {
    return await httpRequest({
      method: 'PATCH',
      url: urlSubst(COMPANIES_API.UPDATE_COMPANY_EMAIL_GROUP_MEMBER, { companyId, companyEmailGroupId, companyEmailGroupMemberId }),
      data: {
        email: email,
      },
    })
  }

  return useMutation({ mutationFn, ...mutationOptions })
}
